/**
 * Type roles from UI Kit #type. A role is the only way to pick size, weight,
 * leading, tracking and colour together. If copy has no matching role, add one
 * here (and in the kit) — do not invent a one-off style at the call site.
 */

export const TYPE_GROUPS = [
  {
    id: 'titles',
    label: 'Titles',
    note: 'Never 900 — that weight is reserved for meta/numeral.',
    items: [
      {
        role: 'title/landing',
        use: 'Home wordmark. One per landing page.',
        spec: 'display / bold 700 / lh tight / ls tighter / text',
        sample: 'secret santa',
      },
      {
        role: 'title/page',
        use: 'Screen H1. One per page.',
        spec: '5xl · 32px / extrabold 800 / lh tight / ls tighter / text',
        sample: 'Sign in to Secret Santa',
      },
      {
        role: 'title/section',
        use: 'Section H2.',
        spec: '3xl · 21px / extrabold 800 / lh tight / ls tight / text',
        sample: 'How it works',
      },
      {
        role: 'title/card',
        use: 'Card, panel or modal title.',
        spec: '2xl · 19px / extrabold 800 / lh snug / ls tight / text',
        sample: 'Flat 4B',
      },
      {
        role: 'title/sub',
        use: 'H3 inside a card, or a field group.',
        spec: 'xl · 16px / bold 700 / lh snug / ls tight / text',
        sample: 'Players and budget',
      },
      {
        role: 'title/eyebrow',
        use: 'Kicker above an H2. Always caps.',
        spec: '2xs · 11px / extrabold 800 / lh none / ls widest / text-subtle / uppercase',
        sample: 'HOW IT WORKS',
      },
    ],
  },
  {
    id: 'body',
    label: 'Body',
    note: 'Only these sizes. Inside a paragraph: regular or medium, never semibold.',
    items: [
      {
        role: 'body/lead',
        use: 'Lede under an H1, opening paragraph.',
        spec: 'xl · 16px / medium 500 / lh relaxed / text-secondary',
        sample: 'Create a game, send the link, and get a pair in a minute.',
      },
      {
        role: 'body/base',
        use: 'Default interface copy.',
        spec: 'lg · 15px / medium 500 / lh normal / text-secondary',
        sample: 'The draw runs automatically on 20 December at 12:00.',
      },
      {
        role: 'body/sm',
        use: 'Tight spots: tables, lists, a footnote on a card.',
        spec: 'md · 14px / medium 500 / lh normal / text-secondary',
        sample: 'Six players, budget 2000 ₽.',
      },
      {
        role: 'body/strong',
        use: 'Emphasis inside a sentence. Not a paragraph of its own.',
        spec: 'inherit / bold 700 / color text',
        sample: 'by 20 December',
      },
    ],
  },
  {
    id: 'labels',
    label: 'Labels & controls',
    note: 'Text inside interactive elements. The control picks the weight, not the author.',
    items: [
      {
        role: 'label/field',
        use: 'Caption above a field.',
        spec: 'sm · 13px / medium 500 / lh tight / text',
        sample: 'Email',
      },
      {
        role: 'label/control',
        use: 'Copy next to a checkbox, radio or switch.',
        spec: 'md · 14px / medium 500 / lh normal / text',
        sample: 'Remember me on this device',
      },
      {
        role: 'label/input',
        use: 'The value the user types. Size is md on product fields, xl on auth.',
        spec: 'md–xl / semibold 600 / lh snug / text',
        sample: 'anna@example.com',
      },
      {
        role: 'label/placeholder',
        use: 'Hint in an empty field. Applied as ::placeholder CSS — not this component.',
        spec: 'md–xl / medium 500 / text-subtle',
        sample: 'e.g. Flat 4B',
      },
      {
        role: 'label/button-lg',
        use: 'Large button — the primary action on a screen.',
        spec: 'xl · 16px / extrabold 800 / lh none — primary only',
        sample: 'Create a game',
      },
      {
        role: 'label/button-md',
        use: 'Medium button.',
        spec: 'lg · 15px / extrabold 800 / lh none',
        sample: 'Invite everyone',
      },
      {
        role: 'label/button-sm',
        use: 'Small button, a chip action.',
        spec: 'sm · 13px / extrabold 800 / lh none',
        sample: 'Copy',
      },
      {
        role: 'label/button-secondary',
        use: 'Outlined or soft button: one step lighter than primary.',
        spec: 'xl–sm / bold 700 / lh none / text-secondary',
        sample: 'How it works',
      },
      {
        role: 'label/ghost',
        use: 'Ghost button: cancel, back.',
        spec: 'lg · 15px / semibold 600 / lh none / text-muted',
        sample: 'Cancel',
      },
      {
        role: 'label/nav',
        use: 'Header link, tab, menu item.',
        spec: 'md · 14px / bold 700 / text-muted',
        sample: 'My games',
      },
      {
        role: 'label/link-inline',
        use: 'Inline link — surrounding text colour, underlined.',
        spec: 'inherit / bold 700 / inherit / underline',
        sample: 'Create an account',
      },
      {
        role: 'label/link-quiet',
        use: 'Secondary link: “Forgot password?”.',
        spec: 'xs · 12px / bold 700 / text-muted',
        sample: 'Forgot password?',
      },
      {
        role: 'label/tag',
        use: 'Filter chip, status. Small tags pass size="sm".',
        spec: 'md · 14px / semibold 600 / lh none',
        sample: 'Waiting for the draw',
      },
    ],
  },
  {
    id: 'meta',
    label: 'Meta',
    note: 'Anything below 13px is supporting only. Never the only carrier of meaning.',
    items: [
      {
        role: 'meta/helper',
        use: 'Caption under a field.',
        spec: 'xs · 12px / semibold 600 / lh tight / text-subtle',
        sample: 'Everyone in the game will see this.',
      },
      {
        role: 'meta/error',
        use: 'Field or form error.',
        spec: 'xs–md / semibold 600 / lh snug / danger-text',
        sample: 'Check the address',
      },
      {
        role: 'meta/success',
        use: 'Confirmation.',
        spec: 'xs–md / semibold 600 / lh snug / success-text',
        sample: 'Pair assigned',
      },
      {
        role: 'meta/legal',
        use: 'Legal footnote, footer.',
        spec: 'xs · 12px / semibold 600 / lh relaxed / text-subtle',
        sample: 'By continuing you agree to the processing of personal data.',
      },
      {
        role: 'meta/caps',
        use: 'Micro-heading for a group, a section label in the UI.',
        spec: '2xs · 11px / extrabold 800 / ls widest / text-subtle / uppercase',
        sample: 'PLAYERS',
      },
      {
        role: 'meta/mono',
        use: 'Code, a token, an invite link, an id.',
        spec: 'mono / xs · 12px / semibold 600 / text-muted',
        sample: 'santa.app/g/4b',
      },
      {
        role: 'meta/numeral',
        use: 'Large figure: a counter, a step, a timer.',
        spec: '6xl · 40px / heavy 900 / lh none / ls tighter / accent',
        sample: '12',
      },
    ],
  },
] as const;

export const TYPE_RULES = [
  'Weight 900 (heavy) only for meta/numeral. Product screens cap at 800. The landing wordmark is 700.',
  'Nothing below 11px. 11–12px is caps labels, helper text and legal only.',
  'Text colour comes from the role: text → text-secondary → text-muted → text-subtle. Inline links inherit their surrounding text colour and use an underline. Coral is reserved for meta/numeral.',
  'Negative tracking only at 16px and up. Caps text always uses --letter-spacing-widest.',
  'Leading: tight for titles, snug for one or two lines, normal/relaxed for paragraphs. One role, one leading.',
  'No prose at semibold. Semibold is a field value or a chip, not a paragraph.',
] as const;

export type TypeGroupId = (typeof TYPE_GROUPS)[number]['id'];
export type TypeRole = (typeof TYPE_GROUPS)[number]['items'][number]['role'];

export const TYPE_ROLES = TYPE_GROUPS.flatMap((group) => [...group.items]);
